import { BaseLayer } from './base-layer'
import { NeuroglancerSegmentationUserLayer } from '../neuroglancer-type-mappings'
import { GenericLayer } from './generic-layer'
import { LayerType } from './layer-types'
import { defaultDebounceTimeSkipFirst } from '../../utils/rxjs-pipelines'
import { LayerPropertyStoreConnector } from '../../utils/layer-property-store-connector'
import { Store } from '@ngrx/store'
import {
    TrackableService,
    WatchableValue,
} from '../../../case-viewer/neuroglancer-frame/trackable.service'
import {
    optionalHexStringToRgbArray,
    optionalRgbArrayToHexString,
    percentToValue,
    valueToPercent,
} from '../../utils/color-utils'

export class SegmentationLayer extends BaseLayer implements GenericLayer {
    readonly type = LayerType.Segmentation

    readonly opacityPercent: WatchableValue<number>
    readonly opacityUnselectedPercent: WatchableValue<number>
    readonly saturationPercent: WatchableValue<number>
    readonly segmentationColor: WatchableValue<string | null>

    readonly shaderColor = null
    readonly setShaderColor = null
    readonly brightness = null
    readonly contrast = null

    constructor(
        layer: NeuroglancerSegmentationUserLayer,
        store: Store,
        trackable: TrackableService
    ) {
        const storeConnectors = new LayerPropertyStoreConnector(store)
            .addVisibility()
            .addOpacity()
            .addOpacityUnselected()
            .addSaturation()
            .addSegmentationColor()
            .build()
        super(layer.managedLayer, storeConnectors, trackable)

        this.opacityPercent = trackable.wrapWatchableValueWithConversion(
            layer.displayState.selectedAlpha,
            {
                fromNeuroglancer: valueToPercent,
                toNeuroglancer: percentToValue,
            }
        )
        this.opacityPercent.value$.pipe(defaultDebounceTimeSkipFirst()).subscribe(opacity => {
            storeConnectors.dispatchOpacity?.(this.name, percentToValue(opacity))
        })

        this.opacityUnselectedPercent = trackable.wrapWatchableValueWithConversion(
            layer.displayState.notSelectedAlpha,
            {
                fromNeuroglancer: valueToPercent,
                toNeuroglancer: percentToValue,
            }
        )
        this.opacityUnselectedPercent.value$
            .pipe(defaultDebounceTimeSkipFirst())
            .subscribe(opacity => {
                storeConnectors.dispatchOpacityUnselected?.(this.name, percentToValue(opacity))
            })

        this.saturationPercent = trackable.wrapWatchableValueWithConversion(
            layer.displayState.saturation,
            {
                fromNeuroglancer: valueToPercent,
                toNeuroglancer: percentToValue,
            }
        )
        this.saturationPercent.value$.pipe(defaultDebounceTimeSkipFirst()).subscribe(saturation => {
            storeConnectors.dispatchSaturation?.(this.name, percentToValue(saturation))
        })

        this.segmentationColor = trackable.wrapWatchableValueWithConversion(
            layer.displayState.segmentDefaultColor,
            {
                fromNeuroglancer: optionalRgbArrayToHexString,
                toNeuroglancer: optionalHexStringToRgbArray,
            }
        )
        this.segmentationColor.value$
            .pipe(defaultDebounceTimeSkipFirst())
            .subscribe(segmentationColor => {
                storeConnectors.dispatchSegmentationColor?.(this.name, segmentationColor)
            })
    }
}
